import React, { useState } from "react";
import { Link, Text, Toggle, Tooltip } from "@merge-api/merge-javascript-shared";
import { Info } from "lucide-react";
import { UserType } from "../../../../../../models/Entities";
import { CONFIGURATION_FIELD_MAPPINGS_PATH } from "../../../../../../router/RouterUtils";
import useAppContext from "../../../../../context/useAppContext";
import useProductRestrictions from "../../../../../shared/hooks/useProductRestrictions";
import RedactUnmappedDataModal from "../../RedactUnmappedDataModal";

/**
 * Org-level "Redact unmapped data" control in the Common Model scopes header. Toggling opens a
 * confirmation modal; the modal flips the setting and refetches the user, so the toggle stays driven
 * by org state (disableDefaultOnChange). Renders only for admins whose org has the redaction feature.
 */
const RedactUnmappedDataToggle = () => {
  // state
  const [showModal, setShowModal] = useState(false);

  // hooks
  const { user } = useAppContext();
  const { productRestrictions } = useProductRestrictions();

  // derived state
  const isAdmin = user.type === UserType.admin_with_billing;
  const isRedactionFeatureEnabled = productRestrictions?.redact_unmapped_data_enabled ?? false;
  const isRedactionEnabled = user.organization.redact_unmapped_data_enabled;

  if (!isRedactionFeatureEnabled || !isAdmin) return null;

  const redactionTooltip = (
    <Text variant="sm">
      Redact all fields in API logs and Remote Data that are not mapped to Merge Common Model fields
      or used in{" "}
      <Link href={CONFIGURATION_FIELD_MAPPINGS_PATH} target="_blank">
        Field Mapping
      </Link>
      .
    </Text>
  );

  // render
  return (
    <div className="flex items-center gap-x-2">
      <Toggle
        checked={isRedactionEnabled}
        onChange={() => setShowModal(true)}
        disableDefaultOnChange
        labelPlacement="right"
        label={
          <div className="flex items-center gap-x-1">
            <Text variant="sm">Redact unmapped data</Text>
            <Tooltip variant="white" title={redactionTooltip} className="flex items-center">
              <Info size={12} />
            </Tooltip>
          </div>
        }
      />
      <RedactUnmappedDataModal
        isRedactionCurrentlyEnabledForOrg={!!isRedactionEnabled}
        organizationId={user.organization.id}
        isModalOpen={showModal}
        onHide={() => setShowModal(false)}
        onConfirmResponse={() => setShowModal(false)}
      />
    </div>
  );
};

export default RedactUnmappedDataToggle;
